<template>
<div class="container">
  <div class="row">
    <div class="col">
      <b-alert variant="success" dismissible :show="showResponse" @dismissed="showResponse=false">
        <p>予約を登録しました</p>
      </b-alert>
      <b-alert variant="danger" dismissible :show="showError" @dismissed="showError=false">
        <p>予約に失敗しました<template v-if="lastError">: {{ lastError }}</template></p>
      </b-alert>
      <b-alert variant="danger" dismissible :show="showSearchError" @dismissed="showSearchError=false">
        <p>検索に失敗しました<template v-if="searchError">: {{ searchError }} <strong>座標を直接指定してください</strong></template></p>
      </b-alert>
      <b-form @submit.prevent="onSubmit" @reset="onReset">
        <b-form-group label="望遠鏡">
          <b-form-select v-model="form.telescopeId" :options="telescopeList" required @change="$emit('telescopeChanged', $event)"></b-form-select>
        </b-form-group>

        <b-form-group label="撮影日時">
          <div class="form-row">
            <b-input-group class="col-sm-4">
              <b-form-select v-model="form.start.date" :options="dateList" @change="angleChanged('date', $event)" required></b-form-select>
            </b-input-group>
            <b-input-group class="col-sm-2" append="時">
              <b-form-select v-model="form.start.hour" :options="startOptions.hour" @change="angleChanged('hour', $event)" required></b-form-select>
            </b-input-group>
            <b-input-group class="col-sm-2" append="分">
              <b-form-select v-model="form.start.minute" :options="startOptions.minute" @change="angleChanged('minute', $event)" required></b-form-select>
            </b-input-group>
            <b-input-group class="col-sm-2" append="秒">
              <b-form-select v-model="form.start.second" :options="startOptions.second" @change="angleChanged('second', $event)" required></b-form-select>
            </b-input-group>
          </div>
        </b-form-group>

        <b-form-group label="撮影天体">
          <b-form @submit.prevent="search">
            <b-input-group>
              <b-form-input type="text" v-model="form.search" placeholder="天体検索 (e.g. M42, NGC1976)">
              </b-form-input>
              <b-button variant="secondary" v-on:click="search" :disabled="disableSearch">検索</b-button>
            </b-input-group>
          </b-form>
          <b-form-group label="座標">
            <div class="form-row">
              <b-input-group class="col-sm-4" prepend="赤経/RA">
                <b-form-input type="text" v-model="form.ra" required placeholder="[0.0, 360.0]" pattern="\d{1,3}(\.\d+)?" title="0.0〜360.0の範囲で入力してください" @input="angleChanged('ra', $event)">
                </b-form-input>
              </b-input-group>
              <b-input-group class="col-sm-4" prepend="赤緯/Dec">
                <b-form-input type="text" v-model="form.dec" required placeholder="[-90.0, 90.0]" pattern="[+\-]?\d{1,2}(\.\d+)?" title="+90.0〜-90.0の範囲で入力してください" @input="angleChanged('dec', $event)">
                </b-form-input>
              </b-input-group>
              <b-input-group class="col-sm-4" prepend="高度">
                <b-form-input v-bind:class="risedStyle" type="text" v-model="currentAngle" disabled></b-form-input>
              </b-input-group>
            </div>
          </b-form-group>
        </b-form-group>

        <b-form-group label="露出時間">
          <b-input-group append="秒">
            <b-form-input type="text" v-model="form.exposure" required placeholder="撮影対象">
            </b-form-input>
          </b-input-group>
        </b-form-group>

        <b-form-group label="撮影枚数">
          <b-input-group append="枚">
            <b-form-input type="text" v-model="form.take" required placeholder="撮影対象">
            </b-form-input>
          </b-input-group>
        </b-form-group>

        <b-button type="submit" variant="primary" :disabled="disableSubmit">予約</b-button>
        <b-button type="reset" variant="danger" :disabled="disableSubmit">リセット</b-button>
      </b-form>
    </div>
  </div>
</div>
</template>
<style scoped>
.notrised {
    color: red;
}
</style>
<script>
import moment from 'moment'
import axios from 'axios'

function parseDisplayDateToUTCDate(year, month, day, hour, minute, second, timezone) {
  timezone = timezone || 0;
  const d = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  return new Date(d.getTime() - timezone * 1000);
}

// common
function degrees(radian) {
    return radian * (180 / Math.PI);
}
function radians(degree) {
    return degree * (Math.PI / 180);
}
// visiblity
function calcHeight(theta, ra, dec, lat) {
    const H = radians(theta - ra);
    const rdec = radians(dec);
    const rlat = radians(lat);
    const sinh = Math.sin(rlat) * Math.sin(rdec) + Math.cos(rlat) * Math.cos(rdec) * Math.cos(H);
    return degrees(Math.asin(sinh));
}
const SIDEREAL_DAY = 86164.091;
function localSiderealTime(time, epoch) {
    const d = (time - epoch.At) * (24 * 60 * 60) / SIDEREAL_DAY;
    let theta = (epoch.Theta + 360 * d) % 360;
    if (theta < 0) {
        theta += 360;
    }
    if (theta >= 360) {
        theta -= 360;
    }
    return theta;
}
// jd
// from astropy._erfa.cal2jd.c
function date2jd(date) {
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth() + 1;
    const day = date.getUTCDate();
    const hour = date.getUTCHours();
    const minute = date.getUTCMinutes();
    const second = date.getUTCSeconds();
    // Earliest year allowed (4800BC)
    const IYMIN = -4799;
    // Month lengths in days
    const mtab = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    // Validate year and month.
    if (year < IYMIN) {
        throw new Error("bad year");
    }
    if (month < 1 || month > 12) {
        throw new Error("bad month");
    }
    // If February in a leap year, 1, otherwise 0.
    const leap = ((month == 2) && (year % 4 == 0) && (year % 100 != 0 || year % 400 == 0));
    // Validate day, taking into account leap years.
    if (day < 1 || day > (mtab[month - 1] + (leap ? 1 : 0))) {
        throw new Error("bad day");
    }
    // Return result.
    const my = (month <= 2 ? -1 : 0);
    const iypmy = year + my;
    const djm = Math.floor((1461 * (iypmy + 4800)) / 4)
        + Math.floor((367 * (month - 2 - 12 * my)) / 12)
        - Math.floor((3 * Math.floor((iypmy + 4900) / 100)) / 4)
        + day - 2432076;
    return 2400000.5 + djm + ((second / 60 + minute) / 60 + hour) / 24;
}

export default {
  name: 'booking-form',
  props: ['telescopeId', 'telescopeList', 'limitAngle', 'location', 'dateList', 'epochDict'],
  data() {
    let day = moment()
    if ( day.hours() >= 12 ) {
        day = day.add(1, "days");
    }
    let genNum = (num) => {
      let list = []
      for (let i = 0; i < num; i++) { list.push(i + 1) }
      return list
    }
    return {
      lastError: "",
      showResponse: false,
      showError: false,
      disableSubmit: false,
      searchError: "",
      showSearchError: false,
      disableSearch: false,

      form: {
        telescope: "南阿蘇ルナ天文台 82cm",
        // telescope_id: "63ce0ad6-8eb4-4007-9c57-4e1c6eb2d8cd",
        telescopeId: this.telescopeId,
        target: "M45",
        exposure: 30,
        take: 1,
        start: {
          date: day.format('YYYY-MM-DD'),
          hour: 23,
          minute: 0,
          second: 0
        },
      },
      currentAngle: null,
      risedStyle: {
        notrised: true,
      },

      startOptions: {
        date: [],
        hour: [0].concat(genNum(23)).map((v) => {return {text: v, value: v}}),
        minute: [0].concat(genNum(59)).map((v) => {return {text: v, value: v}}),
        second: [0].concat(genNum(59)).map((v) => {return {text: v, value: v}})
      }
    }
  },
  methods: {
    search() {
      this.resetSearch();
      let query = {
        q: this.form.search,
      };
      this.beginSearch();
      this.$store.state.loginUser.session.user.getIdToken(true).then((idToken) => {
        axios.post(this.$store.state.config.API_ROOT + "/objects/search", query, {
          headers: {
            "Authorization": "Bearer " + idToken,
          }
        }).then((response) => {
          if (response.length != 1) {
            // multiple object returns
          }
          for (let key in response.data) {
            this.form.ra = response.data[key].RA;
            this.form.dec = response.data[key].Dec;
            break;
          }
          this.endSearch();
        }).catch((error) => {
          if ( error.response.status == 404 ) {
            this.onSearchError("未登録のオブジェクトです");
          } else {
            this.onSearchError(`不明なエラー（${ error.response.status }）`);
          }
        });
      });
    },
    onSubmit() {
      // dismiss alerts
      this.resetResponse();

      let date = this.form.start.date.split("-");
      let booking = {
        exposure: this.form.exposure,
        take: this.form.take,
        year: date[0],
        month: date[1],
        day: date[2],
        hour: this.form.start.hour,
        minute: this.form.start.minute,
        second: this.form.start.second,
        ra: this.form.ra,
        dec: this.form.dec
      }

      this.beginSubmit();
      this.$store.state.loginUser.session.user.getIdToken(true).then((idToken) => {
        axios.post(this.$store.state.config.API_ROOT + "/schedule/add/" + this.form.telescopeId, booking, {
          headers: {
            "Authorization": "Bearer " + idToken
          }
        }).then((response) => {
          this.$emit('addedBooking', response.data)
          this.onSuccess();
        }).catch((error) => {
          let code = error.response.status;
          if (code == 403) {
            this.onError("認証に失敗しました");
          } else if (code == 405) {
            this.onError("送信に失敗しました");
          } else if (code == 404) {
            this.onError("不明な望遠鏡です");
          } else if (code == 400) {
            if (error.response.data.startsWith("not enough parameter") ||
                error.response.data.startsWith("wrong parameter")) {
              this.onError("パラメータエラー");
            } else {
              this.onError("パラメータエラー （不明なエラー）");
            }
          } else if (code == 412) {
            if (error.response.data.startsWith("expiration check failed")) {
              this.onError("登録期限を超えています");
            } else if (error.response.data.startsWith("out of service")) {
              this.onError("サービス提供期間外です");
            } else if (error.response.data.startsWith("service in suspend")) {
              this.onError("サービス停止期間です");
            } else if (error.response.data.startsWith("too long order per a day")) {
              this.onError("1日あたりの撮影時間を超過しています");
            } else if (error.response.data.startsWith("too much order per a day")) {
              this.onError("1日あたりの撮影枚数を超過しています");
            } else if (error.response.data.startsWith("too short exposure")) {
              this.onError("露出時間が短すぎます");
            } else if (error.response.data.startsWith("too long exposure")) {
              this.onError("露出時間が長すぎます");
            } else if (error.response.data.startsWith("too short total exposure")) {
              this.onError("トータル露出時間が短すぎます");
            } else if (error.response.data.startsWith("too long total exposure")) {
              this.onError("トータル露出時間が長すぎます");
            } else if (error.response.data.startsWith("too little order")) {
              this.onError("撮影枚数が少なすぎます");
            } else if (error.response.data.startsWith("too much order")) {
              this.onError("撮影枚数が多すぎます");
            } else if (error.response.data.startsWith("Conflicting request")) {
              this.onError("予約が重複しています");
            } else if (error.response.data.startsWith("no such object")) {
              this.onError("未登録の天体です");
            } else if (error.response.data.startsWith("bad angle")) {
              this.onError("撮影基準高度に達していません");
            } else {
              this.onError("前提条件で失敗しました （不明なエラー）");
            }
          } else {
            this.onError("不明なエラー");
          }
        });
      }).catch((error) => {
        this.onError("セッションが無効です");
      });
      return false;
    },
    onReset() {
      let day = moment();
      if (day.hours() >= 12) {
          day = day.add(1, "days");
      }
      this.form = {
        telescope: "南阿蘇ルナ天文台 82cm",
        telescopeId: "63ce0ad6-8eb4-4007-9c57-4e1c6eb2d8cd",
        target: "M45",
        exposure: 30,
        number: 1,
        start: {
          date: day.format("YYYY-MM-DD"),
          hour: 23,
          minute: 0,
          second: 0
        }
      }
    },
    beginSubmit() {
      this.disableSubmit = true;
      this.$forceUpdate();
    },
    endSubmit() {
      this.disableSubmit = false;
    },
    resetResponse() {
      this.lastError = "";
      this.showResponse = false;
      this.showError = false;
    },
    onSuccess() {
      this.endSubmit();
      this.showResponse = true;
      this.$forceUpdate();
    },
    onError(error) {
      this.endSubmit();
      this.lastError = error;
      this.showError = true;
      this.$forceUpdate();
    },
    beginSearch() {
      this.disableSearch = true;
      this.$forceUpdate();
    },
    endSearch() {
      this.disableSearch = false;
    },
    resetSearch() {
      this.searchError = "";
      this.showSearchError = false;
      this.form.ra = null;
      this.form.dec = null;
    },
    onSearchError(error) {
      this.endSearch();
      this.searchError = error;
      this.showSearchError = true;
      this.$forceUpdate();
    },
    angleChanged(type, value) {
      const date = (type == "date") ? value : this.form.start.date;
      const hour = (type == "hour") ? value : this.form.start.hour;
      const minute = (type == "minute") ? value : this.form.start.minute;
      const second = (type == "second") ? value : this.form.start.second;
      const ra = parseFloat((type == "ra") ? value : this.form.ra);
      const dec = parseFloat((type == "dec") ? value : this.form.dec);
      const epoch = this.epochDict[date];
      if ( ! epoch || isNaN(ra) || isNaN(dec) || ! this.location.Latitude ) {
        this.currentAngle = null;
        return;
      }
      const t = date.split("-");
      const d = parseDisplayDateToUTCDate(parseInt(t[0]), parseInt(t[1]), parseInt(t[2]), hour, minute, second, this.location.Timezone);
      const jd = date2jd(d);
      const lst = localSiderealTime(jd, epoch);
      this.currentAngle = calcHeight(lst, parseFloat(ra), parseFloat(dec), this.location.Latitude).toFixed(0);
      this.risedStyle.notrised = this.currentAngle < this.limitAngle;
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/BookingForm.vue